<template>
  <div class="accordion-container">
    <button
      :id="`accordion-button-${id}`"
      class="accordion-button"
      :class="{ 'is-expanded': isExpanded }"
      :aria-expanded="isExpanded"
      :aria-controls="`accordion-content-${id}`"
      @click="toggle"
    >
      <span class="accordion-title">{{ title }}</span>
      <span class="accordion-icon" :class="{ 'rotated': isExpanded }">▼</span>
    </button>
    <div
      :id="`accordion-content-${id}`"
      class="accordion-content"
      :class="{ 'is-expanded': isExpanded }"
      :aria-labelledby="`accordion-button-${id}`"
      :hidden="!isExpanded"
    >
      <div class="accordion-body">
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';

const props = defineProps({
  id: {
    type: String,
    required: true
  },
  title: {
    type: String,
    required: true
  },
  defaultExpanded: {
    type: Boolean,
    default: false
  }
});

const isExpanded = ref(props.defaultExpanded);

const toggle = () => {
  isExpanded.value = !isExpanded.value;
};
</script>

<style scoped>
.accordion-container {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
  background: white;
}

.accordion-button {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 18px;
  background: white;
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: 1rem;
  font-weight: 600;
  color: var(--an-text-primary);
  transition: background-color 0.2s ease;
}

.accordion-button:hover {
  background: #f8f9fa;
}

.accordion-button:focus {
  outline: 2px solid var(--an-primary);
  outline-offset: -2px;
}

.accordion-button.is-expanded {
  background: #f0f4f8;
  border-bottom: 1px solid #e0e0e0;
}

.accordion-title {
  flex: 1;
  line-height: 1.4;
}

.accordion-icon {
  flex-shrink: 0;
  margin-left: 12px;
  font-size: 0.75rem;
  color: var(--an-primary);
  transition: transform 0.3s ease;
}

.accordion-icon.rotated {
  transform: rotate(180deg);
}

.accordion-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
}

.accordion-content.is-expanded {
  max-height: 2000px;
  transition: max-height 0.5s ease-in;
}

.accordion-body {
  padding: 18px;
  line-height: 1.6;
  color: var(--an-text-primary);
  font-size: 0.95rem;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .accordion-button {
    padding: 14px 16px;
    font-size: 0.95rem;
  }

  .accordion-body {
    padding: 16px;
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .accordion-button {
    padding: 12px 14px;
    font-size: 0.9rem;
  }

  .accordion-body {
    padding: 14px;
    font-size: 0.85rem;
  }

  .accordion-icon {
    font-size: 0.7rem;
  }
}
</style>
